import {
    CellArea,
    ColumnIndex,
    IGridCell,
    IGridModelView,
    OnGetCellArea,
    OnGetToColumnIndex,
    OnGetToRowIndex,
    RowIndex
} from '@mintdata-wsp/common/state/ui/visualDesigner/grid/GridModel';
import {GridModel, GridState} from '@mintdata-wsp/wsp/ui/components/grid/GridModel';
import bind from 'bind-decorator';

export class GridModelView implements IGridModelView
{
    private _offsetToVisibleColumnIndex = null;

    // noinspection TypeScriptAbstractClassConstructorCanBeMadeProtected
    public constructor  (
        private _getCellArea: OnGetCellArea,
        private _state: GridState,
        private _gridModel: GridModel,
        private _fromRowIndex: number,
        private _toRowIndex: OnGetToRowIndex,
        private _fromColumnIndex: number,
        private _toColumnIndex: OnGetToColumnIndex
    )
    {
        this._computeVisibleColumnIndexes();

        this.getAreaAt = this.getAreaAt.bind(this);
        this.getCellAt = this.getCellAt.bind(this);

        this.getRow = this.getRow.bind(this);
        this.getColumn = this.getColumn.bind(this);

        this.getNumColumns = this.getNumColumns.bind(this);
        this.getNumRows = this.getNumRows.bind(this);

        this.getAllRowsHeight = this.getAllRowsHeight.bind(this);
        this.getRowHeight = this.getRowHeight.bind(this);
        this.setRowHeight = this.setRowHeight.bind(this);

        this.setIsRowSelected = this.setIsRowSelected.bind(this);
        this.isRowSelected = this.isRowSelected.bind(this);

        this.getAllColumnsWidth = this.getAllColumnsWidth.bind(this);
        this.getColumnWidth = this.getColumnWidth.bind(this);
        this.setColumnWidth = this.setColumnWidth.bind(this);
    }

    @bind
    private _computeVisibleColumnIndexes()
    {
        const columnFilter = this._state.columnFilter;

        if( columnFilter )
        {
            this._offsetToVisibleColumnIndex = [];

            // If row header column is included, always include it in the
            // filtered column view as well
            if( this._fromColumnIndex === 0 )
                this._offsetToVisibleColumnIndex.push(0);

            for( let columnKey of columnFilter )
            {
                const indexOfColumn = this._gridModel.getIndexOfColumn(columnKey, false);
                this._offsetToVisibleColumnIndex.push(indexOfColumn);
            }
        }
    }

    public getAreaAt(rowIndex:RowIndex, columnIndex:number):CellArea
    {
        return this._getCellArea(rowIndex, columnIndex);
    }

    public getCellAt(rowIndex:RowIndex, columnIndex:number):IGridCell|null
    {
        const eRowIndex = rowIndex + this._fromRowIndex;

        let eColumnIndex = this._fromColumnIndex;
        if( this._offsetToVisibleColumnIndex )
            eColumnIndex += this._offsetToVisibleColumnIndex[columnIndex];
        else
            eColumnIndex += columnIndex;

        if( eRowIndex > this._toRowIndex() )
        {
            console.error(`Error: invalid effective row index: ${eRowIndex}`);
            return null;
        }

        if( eColumnIndex > this._toColumnIndex() )
        {
            console.error(`Error: invalid effective column index: ${eColumnIndex}`);
            return null;
        }

        return this._gridModel.getRawCell(eRowIndex, eColumnIndex);
    }

    public getColumn(columnIndex:ColumnIndex):IGridCell[]
    {
        const column:IGridCell[] = [];

        for( let rowIndex = 0; rowIndex < this.getNumRows(); rowIndex++ )
            column.push(this.getCellAt(rowIndex, columnIndex));

        return column;
    }

    public getRow(rowIndex:RowIndex):IGridCell[]
    {
        const row:IGridCell[] = [];

        for( let columnIndex = 0; columnIndex < this.getNumColumns(); columnIndex++ )
            row.push(this.getCellAt(rowIndex, columnIndex));

        return row;
    }

    public getNumColumns():number
    {
        if( this._offsetToVisibleColumnIndex )
            return this._offsetToVisibleColumnIndex.length;
        else
            return this._toColumnIndex() - this._fromColumnIndex + 1;
    }

    public getNumRows():number
    {
        return this._toRowIndex() - this._fromRowIndex + 1;
    }

    public getAllRowsHeight():number
    {
        let totalHeight:number = 0;

        const numRows = this.getNumRows();
        for( let i = 0; i < numRows; i++ )
            totalHeight += this.getRowHeight(i);

        return totalHeight;
    }

    public getRowHeight(rowIndex:RowIndex):number
    {
        rowIndex += this._fromRowIndex;
        return this._gridModel.getRowHeight(rowIndex);
    }

    public setRowHeight(rowIndex:RowIndex, newHeight:number):void
    {
        rowIndex += this._fromRowIndex;
        this._gridModel.setRowHeight(rowIndex, newHeight);
    }

    public isRowSelected(rowIndex:RowIndex):boolean
    {
        rowIndex += this._fromRowIndex;
        return this._gridModel.isRowSelected(rowIndex);
    }

    public setIsRowSelected(rowIndex:RowIndex, isSelected:boolean):void
    {
        rowIndex += this._fromRowIndex;
        return this._gridModel.setRowIsSelected(rowIndex, isSelected);
    }

    public getAllColumnsWidth():number
    {
        let totalWidth:number = 0;

        const numColumns = this.getNumColumns();
        for( let i = 0; i < numColumns; i++ )
            totalWidth += this.getColumnWidth(i);

        return totalWidth;
    }

    public getColumnWidth(columnIndex:ColumnIndex):number
    {
        columnIndex += this._fromColumnIndex;
        return this._gridModel.getColumnWidth(columnIndex);
    }

    public setColumnWidth(columnIndex:ColumnIndex, newWidth:number):void
    {
        columnIndex += this._fromColumnIndex;
        this._gridModel.setColumnWidth(columnIndex, newWidth);
    }
}
